/**
 * Sample data for the Ai Hire preview. Shapes follow the planned API (Job, Candidate,
 * Application, Interview, Offer) so `aiHireApi.js` can switch to real endpoints later.
 */

/** Preview week: Mon 26 Oct to Sat 31 Oct 2026. Hours are whole numbers (24h). */
export const WEEK_DAYS = [
  { date: "2026-10-26", label: "Mon 26" },
  { date: "2026-10-27", label: "Tue 27" },
  { date: "2026-10-28", label: "Wed 28" },
  { date: "2026-10-29", label: "Thu 29" },
  { date: "2026-10-30", label: "Fri 30" },
  { date: "2026-10-31", label: "Sat 31" },
];
export const DAY_START_HOUR = 9;
export const DAY_END_HOUR = 19;
export const TODAY = "2026-10-27";

export const LOCATIONS = ["Hyderabad", "Bengaluru", "Remote"];

export const JOBS = [
  {
    id: "j1",
    title: "React Developer",
    client: "Acme Realty",
    project: "Website Revamp",
    owner: "Ravi Kumar",
    hiringManager: "Sneha Rao",
    location: "Hyderabad",
    workMode: "Onsite",
    openings: 2,
    status: "Open",
    postedOn: "2026-10-12",
    skills: ["React", "JavaScript", "REST APIs", "CSS"],
  },
  {
    id: "j2",
    title: "UI/UX Designer",
    client: "Acme Realty",
    project: "Website Revamp",
    owner: "Ravi Kumar",
    hiringManager: "Sneha Rao",
    location: "Remote",
    workMode: "Remote",
    openings: 1,
    status: "Open",
    postedOn: "2026-10-15",
    skills: ["Figma", "User research", "Prototyping", "Design systems"],
  },
  {
    id: "j3",
    title: "QA Engineer",
    client: "Acme Realty",
    project: "Mobile App",
    owner: "Ravi Kumar",
    hiringManager: "Arun Das",
    location: "Hyderabad",
    workMode: "Hybrid",
    openings: 1,
    status: "Open",
    postedOn: "2026-10-05",
    skills: ["Manual testing", "Selenium", "API testing", "SQL"],
  },
  {
    id: "j4",
    title: "Digital Marketer",
    client: "Well Bee",
    project: "Social Growth",
    owner: "Anil Mehta",
    hiringManager: "Kavya Pillai",
    location: "Bengaluru",
    workMode: "Hybrid",
    openings: 1,
    status: "On hold",
    postedOn: "2026-10-08",
    skills: ["SEO", "Meta Ads", "Google Ads", "Analytics"],
  },
  {
    id: "j5",
    title: "Content Writer",
    client: "Well Bee",
    project: "Social Growth",
    owner: "Anil Mehta",
    hiringManager: "Kavya Pillai",
    location: "Remote",
    workMode: "Remote",
    openings: 2,
    status: "Open",
    postedOn: "2026-10-18",
    skills: ["Copywriting", "SEO writing", "Research", "Editing"],
  },
  {
    id: "j6",
    title: "Python Developer",
    client: "Tumma Technologies",
    project: "Cloud Office",
    owner: "Rajesh Tumma",
    hiringManager: "Sneha Rao",
    location: "Hyderabad",
    workMode: "Onsite",
    openings: 1,
    status: "Closed",
    postedOn: "2026-09-20",
    skills: ["Python", "Flask", "MySQL", "AWS"],
  },
];

/** `location` is where the interviewer sits; "Online" interviews by video. Days index WEEK_DAYS. */
export const INTERVIEWERS = [
  { id: "i1", name: "Ravi Kumar", location: "Hyderabad", days: [0, 1, 2, 3, 4], from: 10, to: 17 },
  { id: "i2", name: "Sneha Rao", location: "Hyderabad", days: [1, 2, 3, 4, 5], from: 11, to: 18 },
  { id: "i3", name: "Kavya Pillai", location: "Bengaluru", days: [0, 1, 2, 3, 4], from: 10, to: 16 },
  { id: "i4", name: "Arun Das", location: "Online", days: [0, 1, 2, 3, 4, 5], from: 9, to: 19 },
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const hh = (h) => `${String(h).padStart(2, "0")}:00`;

/** Candidate availability: days index WEEK_DAYS; `prefers` are interview locations (incl. "Online"). */
function cand(id, name, location, experienceYears, noticeDays, prefers, days, from, to, skills) {
  const dayLabel =
    days.length > 1 && days[days.length - 1] - days[0] === days.length - 1
      ? `${DAY_NAMES[days[0]]}-${DAY_NAMES[days[days.length - 1]]}`
      : days.map((d) => DAY_NAMES[d]).join(", ");
  const slug = name.toLowerCase().replace(/\s+/g, ".");
  const n = Number(id.slice(1));
  return {
    id,
    name,
    email: `${slug}@example.com`,
    phone: `+91 98480 ${String(10000 + n * 731).slice(-5)}`,
    location,
    experienceYears,
    noticeDays,
    skills,
    preferredLocations: prefers,
    availability: { days, from, to, label: `${dayLabel}, ${hh(from)}-${hh(to)}` },
  };
}

export const CANDIDATES = [
  cand("c1", "Priya Sharma", "Hyderabad", 4, 30, ["Hyderabad"], [1, 2, 3], 10, 13, ["React", "Redux", "TypeScript"]),
  cand("c2", "Meena Iyer", "Bengaluru", 5, 15, ["Bengaluru", "Online"], [0, 1, 2], 14, 18, ["Figma", "UX writing"]),
  cand("c3", "Farhan Ali", "Hyderabad", 2, 0, ["Online"], [0, 1, 2, 3, 4], 10, 18, ["Blogging", "SEO"]),
  cand("c4", "Divya Nair", "Hyderabad", 3, 30, ["Hyderabad"], [2, 3, 4], 11, 15, ["Selenium", "Postman"]),
  cand("c5", "Arjun Reddy", "Vijayawada", 3, 60, ["Online"], [1, 3, 5], 10, 14, ["React", "Node.js"]),
  cand("c6", "Sanjana Gupta", "Bengaluru", 4, 30, ["Bengaluru"], [0, 1], 10, 13, ["Meta Ads", "SEO"]),
  cand("c7", "Vikram Singh", "Hyderabad", 5, 30, ["Hyderabad", "Online"], [2, 3, 4], 14, 18, ["React", "Next.js", "CSS"]),
  cand("c8", "Ananya Bose", "Kolkata", 3, 45, ["Online"], [0, 1, 2], 11, 16, ["Figma", "Prototyping"]),
  cand("c9", "Rohit Jain", "Hyderabad", 2, 15, ["Hyderabad"], [3, 4], 10, 13, ["Manual testing"]),
  cand("c10", "Lakshmi Prasad", "Hyderabad", 4, 30, ["Hyderabad"], [1, 2, 3], 10, 13, ["React", "JavaScript", "Jest"]),
  cand("c11", "Neha Kapoor", "Pune", 6, 30, ["Online"], [0, 1, 2], 14, 18, ["Figma", "Design systems"]),
  cand("c12", "Karthik Menon", "Bengaluru", 3, 30, ["Hyderabad", "Online"], [3, 4, 5], 11, 15, ["Selenium", "API testing"]),
  cand("c13", "Aditya Rao", "Hyderabad", 4, 30, ["Hyderabad"], [2, 4], 14, 18, ["React", "REST APIs"]),
  cand("c14", "Pooja Desai", "Mumbai", 3, 15, ["Online"], [3, 4], 10, 13, ["Copywriting", "Editing"]),
  cand("c15", "Suresh Babu", "Hyderabad", 5, 30, ["Hyderabad"], [0, 3], 10, 17, ["Selenium", "SQL", "API testing"]),
  cand("c16", "Rahul Verma", "Bengaluru", 4, 30, ["Bengaluru"], [0, 1], 10, 13, ["Google Ads", "Analytics"]),
  cand("c17", "Swathi Reddy", "Hyderabad", 5, 45, ["Hyderabad"], [0, 1], 14, 17, ["React", "TypeScript", "CSS"]),
  cand("c18", "Kiran Rao", "Hyderabad", 3, 30, ["Hyderabad"], [1, 2], 10, 13, ["Selenium", "SQL"]),
  cand("c19", "Nikhil Joshi", "Jaipur", 2, 15, ["Online"], [0, 1, 2], 10, 13, ["Copywriting", "Research"]),
  cand("c20", "Harish Kumar", "Chennai", 6, 60, ["Online"], [1, 2], 14, 18, ["Figma", "User research"]),
  cand("c21", "Deepa Krishnan", "Hyderabad", 6, 30, ["Hyderabad"], [0, 1], 10, 13, ["Python", "Flask", "AWS"]),
  cand("c22", "Manoj Patil", "Pune", 2, 90, ["Online"], [4, 5], 10, 13, ["JavaScript"]),
  cand("c23", "Sameer Khan", "Delhi", 4, 90, ["Online"], [2, 3], 14, 17, ["Copywriting"]),
];

/** One application per candidate in this preview. Scores are 0-100; null = not run yet. */
export const APPLICATIONS = [
  { id: "a1", candidateId: "c1", jobId: "j1", stage: "offer_accepted", appliedOn: "2026-10-13", aiScreenScore: 88, aiInterviewScore: 86 },
  { id: "a2", candidateId: "c2", jobId: "j2", stage: "applied", appliedOn: "2026-10-26", aiScreenScore: null, aiInterviewScore: null },
  { id: "a3", candidateId: "c3", jobId: "j5", stage: "applied", appliedOn: "2026-10-26", aiScreenScore: null, aiInterviewScore: null },
  { id: "a4", candidateId: "c4", jobId: "j3", stage: "applied", appliedOn: "2026-10-27", aiScreenScore: null, aiInterviewScore: null },
  { id: "a5", candidateId: "c5", jobId: "j1", stage: "ai_screened", appliedOn: "2026-10-22", aiScreenScore: 72, aiInterviewScore: null },
  { id: "a6", candidateId: "c6", jobId: "j4", stage: "ai_screened", appliedOn: "2026-10-21", aiScreenScore: 68, aiInterviewScore: null },
  { id: "a7", candidateId: "c7", jobId: "j1", stage: "ai_interviewed", appliedOn: "2026-10-19", aiScreenScore: 81, aiInterviewScore: 79 },
  { id: "a8", candidateId: "c8", jobId: "j2", stage: "ai_interviewed", appliedOn: "2026-10-20", aiScreenScore: 77, aiInterviewScore: 64 },
  { id: "a9", candidateId: "c9", jobId: "j3", stage: "ai_interviewed", appliedOn: "2026-10-18", aiScreenScore: 70, aiInterviewScore: 58 },
  { id: "a10", candidateId: "c10", jobId: "j1", stage: "shortlisted", appliedOn: "2026-10-16", aiScreenScore: 85, aiInterviewScore: 84 },
  { id: "a11", candidateId: "c11", jobId: "j2", stage: "shortlisted", appliedOn: "2026-10-17", aiScreenScore: 83, aiInterviewScore: 80 },
  { id: "a12", candidateId: "c12", jobId: "j3", stage: "shortlisted", appliedOn: "2026-10-15", aiScreenScore: 76, aiInterviewScore: 77 },
  { id: "a13", candidateId: "c13", jobId: "j1", stage: "interview_scheduled", appliedOn: "2026-10-14", aiScreenScore: 82, aiInterviewScore: 81 },
  { id: "a14", candidateId: "c14", jobId: "j5", stage: "interview_scheduled", appliedOn: "2026-10-19", aiScreenScore: 79, aiInterviewScore: 78 },
  { id: "a15", candidateId: "c15", jobId: "j3", stage: "interviewed", appliedOn: "2026-10-07", aiScreenScore: 84, aiInterviewScore: 82 },
  { id: "a16", candidateId: "c16", jobId: "j4", stage: "finalized", appliedOn: "2026-10-09", aiScreenScore: 80, aiInterviewScore: 81 },
  { id: "a17", candidateId: "c17", jobId: "j1", stage: "finalized", appliedOn: "2026-10-13", aiScreenScore: 86, aiInterviewScore: 83 },
  { id: "a18", candidateId: "c18", jobId: "j3", stage: "offer_released", appliedOn: "2026-10-06", aiScreenScore: 78, aiInterviewScore: 80 },
  { id: "a19", candidateId: "c19", jobId: "j5", stage: "offer_released", appliedOn: "2026-10-18", aiScreenScore: 81, aiInterviewScore: 79 },
  { id: "a20", candidateId: "c20", jobId: "j2", stage: "offer_declined", appliedOn: "2026-10-01", aiScreenScore: 87, aiInterviewScore: 85 },
  { id: "a21", candidateId: "c21", jobId: "j6", stage: "joined", appliedOn: "2026-09-21", aiScreenScore: 90, aiInterviewScore: 88 },
  {
    id: "a22",
    candidateId: "c22",
    jobId: "j1",
    stage: "rejected",
    appliedOn: "2026-10-20",
    aiScreenScore: 63,
    aiInterviewScore: 52,
    rejectReason: "AI interview score below 60",
  },
  {
    id: "a23",
    candidateId: "c23",
    jobId: "j5",
    stage: "rejected",
    appliedOn: "2026-10-19",
    aiScreenScore: 74,
    aiInterviewScore: null,
    rejectReason: "Notice period of 90 days; role needs someone within a month",
  },
];

/** Interviews in the preview week. `day` indexes WEEK_DAYS; hours are 24h. */
export const INTERVIEWS = [
  { id: "v1", applicationId: "a15", round: "Tech", interviewerId: "i1", day: 0, start: 10, end: 11, mode: "Onsite", status: "Completed" },
  { id: "v2", applicationId: "a17", round: "Client", interviewerId: "i2", day: 0, start: 15, end: 16, mode: "Onsite", status: "Completed" },
  { id: "v3", applicationId: "a16", round: "HR", interviewerId: "i3", day: 1, start: 11, end: 12, mode: "Onsite", status: "Completed" },
  { id: "v4", applicationId: "a13", round: "Tech", interviewerId: "i1", day: 2, start: 14, end: 15, mode: "Onsite", status: "Scheduled" },
  { id: "v5", applicationId: "a14", round: "HR", interviewerId: "i4", day: 3, start: 11, end: 12, mode: "Online", status: "Scheduled" },
  { id: "v6", applicationId: "a15", round: "HR", interviewerId: "i2", day: 3, start: 15, end: 16, mode: "Onsite", status: "Scheduled" },
  { id: "v7", applicationId: "a13", round: "HR", interviewerId: "i2", day: 4, start: 16, end: 17, mode: "Onsite", status: "Scheduled" },
  { id: "v8", applicationId: "a18", round: "Client", interviewerId: "i4", day: 1, start: 9, end: 10, mode: "Online", status: "Completed" },
];

/** Scorecards and approval for Finalized applications. Ratings are out of 5; CTC in lakhs per year. */
export const FINALISTS = {
  a16: {
    scorecards: [
      { round: "Tech", rating: 4, by: "Kavya Pillai" },
      { round: "HR", rating: 5, by: "Kavya Pillai" },
    ],
    decision: "Hire",
    expectedCtc: 7,
    proposedCtc: 7,
    canJoin: "2026-12-01",
    approval: "Pending owner",
  },
  a17: {
    scorecards: [
      { round: "Tech", rating: 4, by: "Ravi Kumar" },
      { round: "Client", rating: 4, by: "Sneha Rao" },
    ],
    decision: "Hire",
    expectedCtc: 10,
    proposedCtc: 9.5,
    canJoin: "2026-11-16",
    approval: "Approved by hiring manager",
  },
};

export const OFFERS = [
  { id: "o1", applicationId: "a1", ctc: 11.5, offerDate: "2026-10-20", validTill: "2026-10-27", joiningDate: "2026-11-16", status: "Accepted", owner: "Ravi Kumar" },
  { id: "o2", applicationId: "a18", ctc: 8, offerDate: "2026-10-22", validTill: "2026-10-29", joiningDate: null, status: "Viewed", owner: "Ravi Kumar" },
  { id: "o3", applicationId: "a19", ctc: 6, offerDate: "2026-10-26", validTill: "2026-11-02", joiningDate: null, status: "Sent", owner: "Anil Mehta" },
  {
    id: "o4",
    applicationId: "a20",
    ctc: 9,
    offerDate: "2026-10-14",
    validTill: "2026-10-21",
    joiningDate: null,
    status: "Declined",
    owner: "Ravi Kumar",
    note: "Accepted another offer",
  },
  { id: "o5", applicationId: "a21", ctc: 14, offerDate: "2026-09-30", validTill: "2026-10-07", joiningDate: "2026-10-19", status: "Joined", owner: "Rajesh Tumma" },
];
